import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { debounce, defer, isEqual, some, uniq, without } from 'lodash-es';
import { DateTime } from 'luxon';
import { MonacoEditorModule } from 'ngx-monaco-editor';
import {
  catchError,
  firstValueFrom,
  forkJoin,
  interval,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  throwError
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import { LoadingComponent } from '../../../component/loading/loading.component';
import { SelectPluginComponent } from '../../../component/select-plugin/select-plugin.component';
import { AutofocusDirective } from '../../../directive/autofocus.directive';
import { ResizeHandleDirective } from '../../../directive/resize-handle.directive';
import { EditorComponent } from '../../../form/editor/editor.component';
import { LinksFormComponent } from '../../../form/links/links.component';
import { PluginsFormComponent, writePlugins } from '../../../form/plugins/plugins.component';
import { fillTags, TagsFormComponent } from '../../../form/tags/tags.component';
import { HasChanges } from '../../../guard/pending-changes.guard';
import { Ref } from '../../../model/ref';
import { addressedTo, getMailbox } from '../../../mods/mailbox';
import { AdminService } from '../../../service/admin.service';
import { ExtService } from '../../../service/api/ext.service';
import { RefService } from '../../../service/api/ref.service';
import { TaggingService } from '../../../service/api/tagging.service';
import { BookmarkService } from '../../../service/bookmark.service';
import { ConfigService } from '../../../service/config.service';
import { EditorService } from '../../../service/editor.service';
import { ModService } from '../../../service/mod.service';
import { Store } from '../../../store/store';
import { controlState, controlValue, scrollToFirstInvalid } from '../../../util/form';
import { QUALIFIED_TAGS_REGEX } from '../../../util/format';
import { printError } from '../../../util/http';
import { getVisibilityTags, hasPrefix, hasTag, localTag } from '../../../util/tag';

@Component({
  selector: 'app-submit-dm',
  templateUrl: './dm.component.html',
  styleUrls: ['./dm.component.scss'],
  host: { 'class': 'full-page-form' },
  imports: [
    forwardRef(() => EditorComponent),
    ReactiveFormsModule,
    AutofocusDirective,
    SelectPluginComponent,
    PluginsFormComponent,
    MonacoEditorModule,
    ResizeHandleDirective,
    TagsFormComponent,
    LoadingComponent,
  ]
})
export class SubmitDmPage implements HasChanges {
  config = inject(ConfigService);
  private mod = inject(ModService);
  admin = inject(AdminService);
  private router = inject(Router);
  store = inject(Store);
  bookmarks = inject(BookmarkService);
  private refs = inject(RefService);
  private exts = inject(ExtService);
  private ts = inject(TaggingService);
  private editor = inject(EditorService);
  private fb = inject(UntypedFormBuilder);

  private readonly controlState0 = controlValue(() => this.to);
  protected readonly toMissing = controlState(() => this.to, c => c.touched && !c.value);


  private readonly destroyRef = inject(DestroyRef);
  private _url = 'comment:' + uuid();

  readonly submitted = signal<boolean>(false);
  dmForm: UntypedFormGroup;
  protected readonly dmFormValid = controlState(() => this.dmForm, c => c.valid);
  protected readonly dmFormPristine = controlState(() => this.dmForm, c => c.pristine);
  readonly serverError = signal<string[]>([]);

  readonly editorComponent = viewChild<EditorComponent>('ed');


  readonly preview = signal<string>('');
  readonly previewTitle = signal('');
  readonly editing = signal<boolean>(false);
  readonly autocomplete = signal<{ value: string, label: string }[]>([]);
  readonly submitting = signal(false);
  private submittingSubscription?: Subscription;
  readonly saving = signal(false);
  private savingSubscription?: Subscription;
  readonly completedUploads = signal<Ref[]>([]);
  /**
   * Plugin data the plugin forms are built from.
   */
  readonly pluginValues = signal<Record<string, any> | undefined>(undefined);
  private cursor?: string;
  private showedError = false;
  private addedMailboxes: string[] = [];
  private searching?: Subscription;
  private readonly tagsValue = controlValue<string[]>(() => this.tags);

  constructor() {
    const mod = this.mod;
    const fb = this.fb;

    mod.setTitle($localize`Submit: Direct Message`);
    this.dmForm = fb.group({
      to: ['', [Validators.pattern(QUALIFIED_TAGS_REGEX)]],
      title: [''],
      sources: fb.array([]),
      comment: [''],
      tags: fb.array([]),
    });
    if (this.admin.editing()) {
      interval(5_000).pipe(
        takeUntilDestroyed(),
      ).subscribe(() => {
        if (this.dmForm.dirty) this.saveForLater();
      });
      if (this.store.submit.url()) {
        this.refs.getEditing(this.store.submit.url()).pipe(
          takeUntilDestroyed(),
        ).subscribe(draft => {
          if (!draft) return;
          this._url = draft.url;
          this.cursor = draft.modifiedString;
          const edit: Ref = { ...draft.plugins?.['plugin/editing'], url: draft.url, origin: draft.origin };
          this.setTags(edit.tags || []);
          this.pluginValues.set(edit.plugins);
          this.title.setValue(edit.title || '');
          this.comment.setValue(edit.comment || '');
          for (const s of edit.sources || []) this.sources.push(this.fb.control(s, LinksFormComponent.validators));
          if (hasTag('notes', edit)) return;
          const to = uniq([
            ...addressedTo(edit),
            ...edit.tags?.filter(t => this.admin.getPlugin(t)?.config?.signature && this.admin.getPlugin(t)?.config?.signature != t) || [],
          ]);
          this.setTo(to.join(' '));
          this.bookmarks.setTo(to);
        });
      }
    }
  }

  addCompletedUpload(ref: Ref) {
    this.completedUploads.update(uploads => [...uploads, ref]);
  }

  async saveChanges() {
    if (this.admin.editing() && this.dmForm.dirty) {
      return firstValueFrom(this.refs.saveEdit(this.writeRef(), this.cursor)
        .pipe(map(() => true), catchError(() => of(false))));
    }
    return !this.dmForm?.dirty;
  }

  // Sync the submit params into the form
  private readonly initialize = effect(() => {
    this.store.submit.dmPlugin();
    this.store.submit.to();
    this.store.submit.tags();
    this.store.account.localTag();
    untracked(() => {
      const dmPlugin = this.store.submit.dmPlugin();
      if (dmPlugin) {
        this.setTo(dmPlugin);
      } if (this.store.submit.to().length) {
        this.setTo(this.store.submit.to().join(' '));
      } else {
        this.setTo('');
      }
      const tags = [...this.store.submit.tags(), ...(this.store.account.localTag() ? [this.store.account.localTag()] : [])];
      if (tags.length) this.addTags(tags);
    });
  });

  get to() {
    return this.dmForm.get('to') as UntypedFormControl;
  }

  get title() {
    return this.dmForm.get('title') as UntypedFormControl;
  }

  get sources() {
    return this.dmForm.get('sources') as UntypedFormArray;
  }

  get comment() {
    return this.dmForm.get('comment') as UntypedFormControl;
  }

  get tags() {
    return this.dmForm.get('tags') as UntypedFormArray;
  }

  readonly notes = computed(() => {
    this.controlState0();
    return !this.to.value || this.to.value === this.store.account.tag();
  });

  saveForLater(leave = false) {
    const savedValue = JSON.stringify(this.dmForm.value);
    this.saving.set(true);
    this.savingSubscription = this.refs.saveEdit(this.writeRef(), this.cursor)
      .pipe(catchError(err => {
        this.saving.set(false);
        return throwError(() => err);
      }))
      .subscribe(cursor => {
        this.saving.set(false);
        this.cursor = cursor;
        if (JSON.stringify(this.dmForm.value) === savedValue) this.dmForm.markAsPristine();
        if (leave) this.router.navigate(['/inbox/ref', 'plugin/editing']);
      });
    this.savingSubscription?.add(() => this.saving.set(false));
  }

  writeRef() {
    return <Ref> {
      url: this._url,
      origin: this.store.account.origin(),
      title: this.dmForm.value.title,
      comment: this.dmForm.value.comment,
      sources: this.writeSources(),
      tags: this.dmForm.value.tags,
      plugins: writePlugins(this.dmForm.value.tags, this.dmForm.value.plugins),
    };
  }

  writeSources() {
    const url = this._url;
    const sources = [url, ...uniq([url, ...this.store.submit.sources(), ...this.dmForm.value.sources])];
    return sources.length === 2 ? [] : sources;
  }

  addTags(value: string[]) {
    fillTags(this.fb, this.tags, uniq([...this.tags.value, ...value]));
  }

  setTags(value: string[]) {
    fillTags(this.fb, this.tags, value);
  }

  get showError() {
    return this.to.touched && this.to.errors?.['pattern'];
  }

  validate(input: HTMLInputElement) {
    if (this.showError) {
      input.setCustomValidity($localize`
        User tags must start with the "+user/" or "_user/" prefix.
        Notification tags must start with the "plugin/inbox" or "plugin/outbox" prefix.
        Tags must be lower case letters, numbers, periods and forward slashes.
        Must not or contain two forward slashes or periods in a row.
        (i.e. "+user/bob", "plugin/outbox/dictionary/science", or "_user/charlie@jasperkm.info")`);
      input.reportValidity();
    }
  }

  setTo(value: string) {
    this.to.setValue(value);
    this.getPreview(value);
    this.changedTo(value);
  }

  changedTo(value: string) {
    const notes = !value || value === this.store.account.tag();
    if (notes && !hasTag('notes', this.tags.value)) {
      const newTags = uniq([...without(this.tags.value, ...['dm', 'plugin/thread', ...this.addedMailboxes]), 'notes']);
      this.setTags(newTags);
      this.addedMailboxes = [];
    } else if (!notes) {
      const mailboxes = ['dm', 'plugin/thread', ...value.toLowerCase().split(/[,\s]+/).filter(t => !!t).flatMap((t: string) => this.getMailboxes(t))];
      const added = without(mailboxes, ...this.addedMailboxes);
      const removed = without(this.addedMailboxes, ...mailboxes);
      const newTags = uniq([...without(this.tags.value, ...removed, 'notes'), ...added]);
      this.setTags(newTags);
      this.addedMailboxes = mailboxes;
    }
  }

  preview$(value: string): Observable<{ name?: string, tag: string } | undefined> {
    return this.editor.getTagPreview(value, this.store.account.origin());
  }

  edit(input: HTMLInputElement) {
    this.editing.set(true);
    this.preview.set('');
    input.focus();
  }

  clickPreview(input: HTMLInputElement) {
    if (this.store.hotkey()) {
      this.config.tag(input.value);
    } else {
      this.edit(input);
    }
  }

  search = debounce((input: HTMLInputElement) => {
    const text = input.value.replace(/[,\s]+$/, '');
    const parts = text.split(/[,\s]+/).filter(t => !!t);
    const value = parts.pop() || '';
    const prefix = text.substring(0, text.length - value.length)
    const tag = value.replace(/[^_+a-z0-9./]/, '').toLowerCase();
    this.searching?.unsubscribe();
    this.searching = this.exts.page({
      query: '+user|_user',
      search: tag,
      size: 1,
    }).pipe(
      switchMap(page => page.page.totalElements ? forkJoin(page.content.map(x => this.preview$(x.tag + x.origin))) : of([])),
      map(xs => xs.filter(x => !!x) as { name?: string, tag: string }[]),
    ).subscribe(xs => {
      this.autocomplete.set(xs.map(x => ({ value: prefix + x.tag, label: x.name || x.tag })));
    });
  }, 400);

  blur(input: HTMLInputElement) {
    this.editing.set(false);
    if (this.showError && !this.showedError) {
      this.showedError = true;
      defer(() => this.validate(input));
    } else {
      this.showedError = false;
      this.setTo(input.value);
      if (this.to.valid) this.bookmarks.setTo(input.value.split(/[,\s]+/).filter(t => !!t));
      this.getPreview(input.value) ;
    }
  }

  getPreview(value: string) {
    if (!value) return;
    if (this.showedError) return;
    forkJoin(value.split(/[,\s]+/).filter(t => !!t).map( part => this.preview$(part))).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(xs => {
      this.preview.set(xs.map(x => x?.name || x?.tag || '').join(',  '));
      this.previewTitle.set(value);
    });
  }

  getMailboxes(tag: string): string[] {
    return this.admin.getPlugin(tag)?.config?.reply || [ getMailbox(tag, this.store.account.origin()), ...hasPrefix(tag, '+user') ? [localTag(tag).substring(1)] : [] ];
  }

  readonly codeLang = computed(() => {
    for (const t of this.tagsValue() || []) {
      if (hasPrefix(t, 'plugin/code')) {
        return t.split('/')[2];
      }
    }
    return '';
  });

  readonly codeOptions = computed(() => ({
    language: this.codeLang(),
    theme: this.store.darkTheme() ? 'vs-dark' : 'vs',
    automaticLayout: true,
  }), { equal: isEqual });

  readonly customEditor = computed(() => {
    const tags = this.tagsValue();
    if (!tags) return false;
    return some(this.admin.editor(), t => hasTag(t.tag, tags));
  });

  get top() {
    return this.sources.value[1] || this.sources.value[0] || this._url;
  }

  addSource(value = '') {
    while (this.sources.value.length < 2) {
      this.sources.push(this.fb.control(this.top, LinksFormComponent.validators));
    }
    this.sources.push(this.fb.control(value, LinksFormComponent.validators));
    this.submitted.set(false);
  }

  syncEditor() {
    this.editor.syncEditor(this.fb, this.dmForm);
  }

  submit() {
    if (this.saving()) {
      this.savingSubscription?.add(() => this.submit());
      return;
    }
    this.serverError.set([]);
    this.submitted.set(true);
    this.dmForm.markAllAsTouched();
    if (!this.dmForm.valid) {
      scrollToFirstInvalid();
      return;
    }
    const url = this._url;
    const published = this.dmForm.value.published ? DateTime.fromISO(this.dmForm.value.published) : DateTime.now();
    const sources = this.writeSources();
    const finalTags = this.dmForm.value.tags;
    const ref = {
      url,
      origin: this.store.account.origin(),
      title: this.dmForm.value.title,
      comment: this.dmForm.value.comment,
      sources,
      published,
      tags: finalTags,
      plugins: writePlugins(this.dmForm.value.tags, this.dmForm.value.plugins),
    };
    this.submitting.set(true);
    this.submittingSubscription = (this.cursor ? this.refs.update({ ...ref, modifiedString: this.cursor }) : this.refs.create(ref)).pipe(
      switchMap(res => {
        const finalVisibilityTags = getVisibilityTags(finalTags);
        if (!finalVisibilityTags.length) return of(res);
        const taggingOps = this.completedUploads()
          .map(upload => this.ts.patch(finalVisibilityTags, upload.url, upload.origin));
        if (!taggingOps.length) return of(res);
        return forkJoin(taggingOps).pipe(map(() => res));
      }),
      catchError((res: HttpErrorResponse) => {
        this.submitting.set(false);
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    ).subscribe(() => {
      this.submitting.set(false);
      this.dmForm.markAsPristine();
      this.completedUploads.set([]);

      this.router.navigate(['/ref', url, 'thread'], { queryParams: { published }, replaceUrl: true});
    });
    this.submittingSubscription?.add(() => this.submitting.set(false));
  }
}
